import { latexExportsJson } from "@corca/data/latex-exports";
import { merge } from "es-toolkit";

import { prioritizeSuggestionRules } from "@/input/utils/editOperators";
import { operators, flattenOperators } from "@/models/nodes/operators";
import { isUnitChild } from "@/models/nodes/utils";

import { isSlashMenuActive } from "./slashCommandSuggestionsProvider";
import { checkFilterRules } from "./utils/filterRules";
import { filterSuggestions } from "./utils/filterSuggestions";
import { mapOperatorPropertiesToSuggestion } from "./utils/mapOperatorPropertiesToSuggestion";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";

import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { OperatorTypeValue, KeywordLang } from "@corca/data";

const allOperatorSuggestions: SingleSuggestion<"operator">[] = [];
const keyboardOnlySuggestions = new Set<SingleSuggestion<"operator">>();

for (const operatorType of Object.keys(operators) as OperatorTypeValue[]) {
  const operator = operators[operatorType];

  if (!operator.variants) {
    const suggestion = mapOperatorPropertiesToSuggestion(operator, operatorType, latexExportsJson);
    if (suggestion) allOperatorSuggestions.push(suggestion);
    continue;
  }

  const allKeywords = operator.variants.reduce((acc, variant) => {
    if (variant.keywords) return merge(acc, normalizeSuggestionKeywords(variant));
    return acc;
  }, normalizeSuggestionKeywords(operator));

  for (const variant of operator.variants) {
    const placement = variant.renderPlacement;
    const isListed = !placement || placement.includes("suggestions");
    const isKeyboard = placement?.includes("keyboard") ?? false;

    if (!isListed && !isKeyboard) continue;

    const suggestion = mapOperatorPropertiesToSuggestion(
      operator,
      operatorType,
      latexExportsJson,
      variant,
      allKeywords,
    );

    if (!suggestion) continue;

    allOperatorSuggestions.push(suggestion);
    if (!isListed) keyboardOnlySuggestions.add(suggestion);
  }
}

export const operatorSuggestionsData: SingleSuggestion<"operator">[] =
  allOperatorSuggestions.filter((suggestion) => !keyboardOnlySuggestions.has(suggestion));

/**
 * Includes `keyboard` variants, which apply on a keypress but are never listed.
 */
export const keyboardOperatorSuggestionsData: SingleSuggestion<"operator">[] =
  allOperatorSuggestions;

const editModeOperatorSuggestionsData: SingleSuggestion<"operator">[] = flattenOperators.reduce<
  SingleSuggestion<"operator">[]
>((previousValue, operator) => {
  if (operator.editModeVariant !== null) {
    return [
      ...previousValue,
      {
        type: "single",
        item: {
          type: "operator",
          value: operator.operatorType,
          symbol: operator.symbol,
          intlName: operator.intlName,
        },
        substitution: operator.editModeVariant?.substitution ?? operator.substitution,
        icon: operator.icon,
        filterRules: operator.editModeVariant?.filterRules ?? operator.filterRules,
        priority: 1,
      },
    ];
  }

  return previousValue;
}, []);

const regulateSuggestionPriority = (
  input: string,
  suggestionDataItems: SingleSuggestion<"operator">[],
  doc: DocumentModel,
): SingleSuggestion<"operator">[] => {
  return suggestionDataItems.map((suggestion) => {
    // TODO: move priorityRules to Suggestion and handle them in filterSuggestions
    // along with other types of suggestions
    const priority = prioritizeSuggestionRules(suggestion, doc.cursor);

    return { ...suggestion, priority };
  });
};

const filterOperatorSuggestionDataItems = (
  input: string,
  suggestionDataItems: SingleSuggestion<"operator">[],
  doc: DocumentModel,
  lang?: KeywordLang,
): SingleSuggestion<"operator">[] => {
  // Make default filtering by `keywords` and `name`
  let filteredSuggestions =
    doc.selection.hasSelectedNodes && !input
      ? suggestionDataItems
      : filterSuggestions(input, suggestionDataItems, { lang });
  // Make addional filtering for fraction items
  // that depends on nodes around cursor
  const { index, node } = doc.cursor;
  filteredSuggestions = filteredSuggestions.filter((item) => {
    if (item.filterRules) {
      return checkFilterRules(item.filterRules, {
        index,
        node,
        selectedNodes: doc.selection.selectedNodes,
        selectedRequired: input.length === 0,
        text: doc.input.value,
      });
    }
    return input.length > 0;
  });
  return filteredSuggestions;
};

function getSuggestionData(
  str: string,
  doc: DocumentModel,
  data: SingleSuggestion<"operator">[],
  lang?: KeywordLang,
) {
  let suggestionData = [];
  suggestionData = filterOperatorSuggestionDataItems(str, data, doc, lang);
  suggestionData = regulateSuggestionPriority(str, suggestionData, doc);
  return suggestionData;
}

export function getSuggestionInEditMode(str: string, doc: DocumentModel, lang?: KeywordLang) {
  return getSuggestionData(str, doc, editModeOperatorSuggestionsData, lang)[0];
}

export function getSuggestionInRegularMode(str: string, doc: DocumentModel, lang?: KeywordLang) {
  return getSuggestionData(str, doc, operatorSuggestionsData, lang)[0];
}

export const operatorSuggestionsProvider: SuggestionProvider = {
  type: "operator",
  search: (str, doc, lang) => {
    if (doc.input.editingNode) {
      return [];
    }

    if (doc.selection.hasSelectedNodes && !doc.selection.isMathOnlySelection) {
      return [];
    }

    if (isSlashMenuActive(str, doc)) {
      return [];
    }

    let data = getSuggestionData(str, doc, operatorSuggestionsData, lang);

    if (isUnitChild(doc.cursor.node)) {
      data = data.filter((s) => s.item.props?.operatorType !== "fraction");
    }

    return [data];
  },
};
